import type { ExamArea, Task } from '../../domain/types.ts';
import { PRUEFUNGSBEREICHE, TEIL2_BEREICHE } from '../../content/syllabus/exam.ts';
import { bewerteNachParagraf15, punkteZuNote, type BestehensUrteil } from '../../content/syllabus/exam.ts';
import { abdeckung, holeAtom, reife, schwacheThemen } from '../../content/curriculum/index.ts';
import { bewerte, type Antwort } from '../../domain/interaktiv.ts';
import type { TopicStateRecord } from '../../domain/types.ts';

/**
 * Prüfungssimulation.
 *
 * Zwei Ziele gleichzeitig: den echten Prüfungseindruck erzeugen und eine
 * belastbare Prognose liefern. Der Prüfungsmodus schaltet alle Hilfen ab – keine
 * Hinweise, keine Sofortlösung, Auswertung erst nach Abgabe.
 */

export interface PruefungDefinition {
  id: string;
  bereich: ExamArea;
  titel: string;
  /** Prüfungszeit in Minuten laut Verordnung. */
  minuten: number;
  /** Aufgabenzahl in der Simulation. */
  aufgabenAnzahl: number;
  /** Punkte je Aufgabe. */
  punkteJeAufgabe: number;
}

export const PRUEFUNGEN: PruefungDefinition[] = [
  {
    id: 'systementwurf',
    bereich: 'systementwurf',
    titel: 'Systementwurf',
    minuten: PRUEFUNGSBEREICHE.systementwurf.minutes,
    aufgabenAnzahl: 12,
    punkteJeAufgabe: 1,
  },
  {
    id: 'funktionsanalyse',
    bereich: 'funktionsanalyse',
    titel: 'Funktions- und Systemanalyse',
    minuten: PRUEFUNGSBEREICHE.funktionsanalyse.minutes,
    aufgabenAnzahl: 12,
    punkteJeAufgabe: 1,
  },
  {
    id: 'wiso',
    bereich: 'wiso',
    titel: 'Wirtschafts- und Sozialkunde',
    minuten: PRUEFUNGSBEREICHE.wiso.minutes,
    aufgabenAnzahl: 18,
    punkteJeAufgabe: 1,
  },
];

/** Punkteverteilung einer abgeschlossenen Simulation. */
export interface Pruefungsergebnis {
  pruefungId: string;
  bereich: ExamArea;
  richtig: number;
  gesamt: number;
  punkte: number;
  maxPunkte: number;
  quote: number;
  note: number;
  dauerSekunden: number;
  imZeitbudget: boolean;
  beendetAm: string;
  /** Thema je Aufgabe, für die Fehleranalyse. */
  schwacheThemenIds: string[];
}

export function wertePruefung(params: {
  pruefung: PruefungDefinition;
  richtig: number;
  gesamt: number;
  dauerSekunden: number;
  themenIds: string[];
  falscheThemenIds: string[];
}): Pruefungsergebnis {
  const maxPunkte = params.gesamt * params.pruefung.punkteJeAufgabe;
  const punkte = params.richtig * params.pruefung.punkteJeAufgabe;
  const quote = params.gesamt > 0 ? (punkte / maxPunkte) * 100 : 0;
  return {
    pruefungId: params.pruefung.id,
    bereich: params.pruefung.bereich,
    richtig: params.richtig,
    gesamt: params.gesamt,
    punkte,
    maxPunkte,
    quote,
    note: punkteZuNote(quote),
    dauerSekunden: params.dauerSekunden,
    imZeitbudget: params.dauerSekunden <= params.pruefung.minuten * 60,
    beendetAm: new Date().toISOString(),
    schwacheThemenIds: params.falscheThemenIds,
  };
}

export interface Prognose {
  noten: Record<ExamArea, number>;
  gesamtNote: number;
  teil2Note: number;
  urteil: BestehensUrteil;
  reife: Record<ExamArea, number>;
  abgedeckt: Record<ExamArea, number>;
  /** Offene Lücken, gewichtet nach Prüfungsbedeutung. */
  dringendsteLuecken: { atomId: string; titel: string; bereich: ExamArea; reifegrad: number }[];
  hinweis: string;
}

const ALLE_BEREICHE: ExamArea[] = [...TEIL2_BEREICHE, 'teil1'];

/**
 * Reife-Prognose aus dem Lernstand.
 *
 * Bewusst vorsichtig formuliert: Das ist eine Hochrechnung aus bisherigen
 * Leistungen, keine Zusage. Ohne echte Simulation bleibt der Wert unsicher –
 * das wird im Text auch so gesagt.
 */
export function berechnePrognose(
  zustaende: Map<string, TopicStateRecord>,
  letzteErgebnisse: Pruefungsergebnis[] = [],
): Prognose {
  const reifeWerte = {} as Record<ExamArea, number>;
  const deckung = {} as Record<ExamArea, number>;

  const jetzt = new Date();
  for (const bereich of ALLE_BEREICHE) {
    const r = reife(zustaende, bereich, jetzt);
    const abged = abdeckung(zustaende, bereich, jetzt);
    reifeWerte[bereich] = r;
    deckung[bereich] = abged.quote;
  }

  // Wo eine echte Simulation vorliegt, hat sie Vorrang vor der Hochrechnung.
  const noten = {} as Record<ExamArea, number>;
  for (const bereich of ALLE_BEREICHE) {
    const simulation = letzteErgebnisse.filter((e) => e.bereich === bereich);
    if (simulation.length > 0) {
      noten[bereich] = simulation[simulation.length - 1]!.note;
    } else {
      // Hochrechnung: 100 % Reife entspricht Note 1,0; 0 % entspricht 6,0.
      noten[bereich] = 1.0 + (1 - reifeWerte[bereich]) * 5;
    }
  }

  const urteil = bewerteNachParagraf15(noten);

  const luecken = schwacheThemen(zustaende, 12).map((atom) => ({
    atomId: atom.id,
    titel: atom.titel,
    bereich: atom.bereich,
    reifegrad: reife(zustaende, atom.bereich, jetzt),
  }));

  const ohneSimulation = letzteErgebnisse.length === 0;
  const hinweis = ohneSimulation
    ? 'Noch keine echte Simulation. Die Noten sind eine Hochrechnung aus deinem ' +
      'Lernstand und werden nach der ersten Simulation deutlich belastbarer.'
    : `Prognose auf Basis von ${letzteErgebnisse.length} Simulation(en). ` +
      'Sie beschreibt deine bisherige Leistung, nicht die Prüfung selbst.';

  const gesamtNote =
    (noten.teil1 * PRUEFUNGSBEREICHE.teil1.weightPercent +
      TEIL2_BEREICHE.reduce((s, b) => s + noten[b] * PRUEFUNGSBEREICHE[b].weightPercent, 0)) /
    100;

  const teil2Note =
    TEIL2_BEREICHE.reduce((s, b) => s + noten[b] * PRUEFUNGSBEREICHE[b].weightPercent, 0) /
    TEIL2_BEREICHE.reduce((s, b) => s + PRUEFUNGSBEREICHE[b].weightPercent, 0);

  return {
    noten,
    gesamtNote,
    teil2Note,
    urteil,
    reife: reifeWerte,
    abgedeckt: deckung,
    dringendsteLuecken: luecken,
    hinweis,
  };
}

/**
 * Prüfbar heißt: Die Aufgabe lässt sich am Bildschirm beantworten.
 *
 * Offene und Fallaufgaben verlangen eine Ausarbeitung; in der Simulation
 * würden sie als leere Seite erscheinen, weil es dort kein Freitextfeld gibt.
 * Interaktive Formate und Multiple Choice sind beantwortbar.
 */
function istPruefbar(task: Task): boolean {
  if (task.interaktiv) return true;
  return (task.proposal.options?.length ?? 0) > 0;
}

/**
 * Verteilt Aufgaben eines Bereichs möglichst gleichmäßig über die Kapitel und
 * mischt dabei die Antwortformate.
 *
 * Zwei Dinge sind hier Absicht:
 *
 *   Nach Kapitel, nicht nach Thema. Vorher war der Eimer ein einzelnes Thema.
 *   Bei zwölf Prüfungsaufgaben und weit mehr Themen füllte der erste Durchlauf
 *   alle Plätze mit je einer Aufgabe aus zwölf verschiedenen Themen – und weil
 *   die gerechneten Multiple-Choice-Aufgaben zuerst im Vorrat stehen, bestand
 *   die Prüfung nur aus Ankreuzfragen. Die interaktiven Formate kamen nie dran.
 *
 *   Formate reihum. Innerhalb eines Kapitels wird abwechselnd eine Aufgabe je
 *   Format eingereiht. Sonst drängen die zahlreichen Richtig/Falsch-Aufgaben
 *   die Zuordnungen und Reihenfolgen an das Ende, wo sie nicht mehr zum Zug
 *   kommen.
 */
export function verteileAufgaben(
  bereich: ExamArea,
  verfuegbar: Task[],
  anzahl: number,
): Task[] {
  const proKapitel = new Map<string, Task[]>();
  for (const task of verfuegbar.filter(
    (t) => t.proposal.examArea === bereich && istPruefbar(t),
  )) {
    const atom = holeAtom(task.proposal.topicIds[0] ?? '');
    const id = atom?.kapitelId ?? task.proposal.topicIds[0] ?? task.proposal.examArea;
    proKapitel.set(id, [...(proKapitel.get(id) ?? []), task]);
  }

  const auswahl: Task[] = [];
  const eimer = [...proKapitel.entries()].map(([id, liste]) => ({
    id,
    liste: nachFormatenGemengt(liste),
    index: 0,
  }));
  let fortgesetzt = true;
  while (auswahl.length < anzahl && fortgesetzt) {
    fortgesetzt = false;
    for (const eimerEintrag of eimer) {
      if (auswahl.length >= anzahl) break;
      const task = eimerEintrag.liste[eimerEintrag.index];
      if (!task) continue;
      eimerEintrag.index += 1;
      fortgesetzt = true;
      if (!auswahl.includes(task)) auswahl.push(task);
    }
  }
  return auswahl;
}

/** Reiht die Aufgaben so, dass die Formate abwechseln statt sich zu stauen. */
function nachFormatenGemengt(liste: Task[]): Task[] {
  const jeFormat = new Map<string, Task[]>();
  for (const task of liste) {
    const f = task.proposal.format;
    jeFormat.set(f, [...(jeFormat.get(f) ?? []), task]);
  }
  const spuren = [...jeFormat.values()];
  const gemengt: Task[] = [];
  for (let i = 0; gemengt.length < liste.length; i += 1) {
    let etwas = false;
    for (const spur of spuren) {
      const task = spur[i];
      if (!task) continue;
      etwas = true;
      gemengt.push(task);
    }
    if (!etwas) break;
  }
  return gemengt;
}

/** Die Antworten eines Durchgangs, so wie sie im Speicher liegen. */
export type GespeicherteAntworten = Record<string, string>;

/** Eine gespeicherte Antwort zurücklesen; beschädigter Inhalt zählt als leer. */
export function leseAntwort(roh: string | undefined): Antwort | null {
  if (roh === undefined) return null;
  try {
    return JSON.parse(roh) as Antwort;
  } catch {
    return null;
  }
}

/** Eine Antwort als Zeichenkette ablegen – so übersteht sie ein Neuladen. */
export function schreibeAntwort(a: Antwort): string {
  return JSON.stringify(a);
}

export interface Pruefungsbilanz {
  /** Zahl der vollständig richtigen Aufgaben. */
  richtig: number;
  /** Beantwortet, aber nicht vollständig richtig. */
  falsch: Task[];
  /** Gar nicht beantwortet. */
  offen: Task[];
  /** Themen aus falschen und offenen Aufgaben – die Wiederholungsliste. */
  schwacheThemenIds: string[];
}

/**
 * Zählt einen Durchgang aus.
 *
 * Die Bewertung liegt bei `bewerte`, nicht bei einem Vergleich der
 * Optionskennungen. Nur so zählen auch die interaktiven Formate, und für ein
 * Zuordnungs- oder Lückentext-Ergebnis gibt es keine Kennung, die man
 * vergleichen könnte. Richtig ist eine Aufgabe erst, wenn alles stimmt –
 * Teilpunkte gibt es in der echten Prüfung auch nicht.
 */
export function bilanzieren(
  aufgaben: Task[],
  antworten: GespeicherteAntworten,
): Pruefungsbilanz {
  const falsch: Task[] = [];
  let richtig = 0;
  for (const t of aufgaben) {
    const roh = antworten[t.taskId];
    if (roh === undefined) continue;
    const a = leseAntwort(roh);
    if (a && bewerte(t, a).korrekt) richtig += 1;
    else falsch.push(t);
  }
  const offen = aufgaben.filter((t) => antworten[t.taskId] === undefined);
  return {
    richtig,
    falsch,
    offen,
    schwacheThemenIds: [...falsch, ...offen].flatMap((t) => t.proposal.topicIds),
  };
}
